"use client";

import { FC, HTMLAttributes, useState, ReactNode } from "react";

import clsx from "clsx";

import Image from "next/image";
import { Accordion } from "@mantine/core";

import TButton from "@/components/atoms/buttons/TButton";
import HtmlText from "../common/HtmlText";

import { ITextButton } from "@/components/atoms/buttons/types";
import { isEmpty } from "lodash";

// ----------------------------------------------------------------------------

export type IArticle = {
  heading: string;
  description: ReactNode;
};

export type IAccordianSliderSectionProps = HTMLAttributes<HTMLElement> & {
  heading?: string;
  articles?: IArticle[];
  images?: ({ src: string; alt: string } | undefined)[];
  buttons?: ITextButton[];
};

const AccordianSliderSection: FC<IAccordianSliderSectionProps> = (props) => {
  const { heading, articles, images, buttons, className, ...other } = props;

  const [activeItem, setActiveItem] = useState<number>(0);

  return (
    <section
      className={clsx("container w-full py-5 lg:py-8 xl:py-[50px]", className)}
      {...other}
    >
      <div className="bg-primary-50 px-7 py-10 md:px-10 lg:py-12 lg:pl-14 lg:pr-20 xl:pl-16 xl:pr-24">
        {heading && (
          <h2 className="mb-6 text-base font-normal text-primary-900 lg:mb-12 lg:text-base xl:text-h6">
            {heading}
          </h2>
        )}

        <div className="flex w-full flex-col items-center justify-between gap-12 lg:flex-row lg:gap-16">
          <div className="w-full lg:max-w-[652px]">
            {articles && (
              <Accordion
                value={articles[activeItem].heading}
                chevron={false}
                classNames={{
                  item: "pt-5 first:pt-0 lg:pt-7",
                  label: "py-0 !font-normal",
                  content: "px-0 py-0 pt-4 lg:pt-7",
                }}
              >
                {articles.map((article, idx) => (
                  <Accordion.Item
                    key={idx}
                    classNames={{}}
                    value={article.heading}
                    className="border-0"
                  >
                    <Accordion.Control
                      onMouseEnter={() => setActiveItem(idx)}
                      className={clsx(
                        "w-fit px-0 text-h6 font-normal text-black-400 first:pt-0 lg:text-h5",
                        idx === activeItem && "text-black-900",
                      )}
                    >
                      {article.heading}
                    </Accordion.Control>

                    <Accordion.Panel className="text-base font-normal text-black-900">
                      <HtmlText>{article.description}</HtmlText>
                    </Accordion.Panel>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}
          </div>
          {buttons &&
            images?.[activeItem]?.src &&
            !isEmpty(images) &&
            !isEmpty(buttons[activeItem]) && (
              <div className="flex flex-col items-center lg:-mt-20 lg:items-start">
                {images?.[activeItem]?.src && (
                  <div className="flex w-[216px] justify-center sm:w-[310px]">
                    <Image
                      src={images?.[activeItem]?.src ?? ""}
                      alt={images?.[activeItem]?.alt ?? ""}
                      height={415}
                      width={310}
                      className="h-auto w-auto max-w-full"
                    />
                  </div>
                )}

                {!isEmpty(buttons[activeItem].text) && (
                  <TButton
                    id={buttons[activeItem].id}
                    className="mt-8 lg:mt-14"
                    variant={buttons[activeItem].variant}
                    href={buttons[activeItem].link}
                    target={buttons[activeItem].target}
                    radius={buttons[activeItem].radius}
                  >
                    {buttons[activeItem].text}
                  </TButton>
                )}
              </div>
            )}
        </div>
      </div>
    </section>
  );
};

export default AccordianSliderSection;
